import React, { useEffect, useRef } from "react";
import { useAppContext } from "../context/AppContext";
import emailjs from "emailjs-com";

const EnquiryModal = () => {
  const {
    enquiryModalOpen,
    setEnquiryModalOpen,
    enquiryProducts,
    setEnquiryProducts,
    tempEnquiryData,
    setTempEnquiryData,
    setEnquiryItems,
  } = useAppContext();

  const modalRef = useRef(null);
  const formRef = useRef(null);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setTempEnquiryData((prev) => ({ ...prev, [id]: value }));
  };


  const handleSubmit = (e) => {
    e.preventDefault();


    if (enquiryProducts.length === 0) {
      alert("Please add at least one product to enquiry");
      return;
    }

    if (
      !tempEnquiryData.name ||
      !tempEnquiryData.email ||
      !tempEnquiryData.message
    ) {
      alert("Please fill all required fields (marked with *)");
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(tempEnquiryData.email)) {
      alert("Please enter a valid email address");
      return;
    }

    const templateParams = {
      name: String(tempEnquiryData.name || "").trim(),
      email: String(tempEnquiryData.email || "").trim(),
      phone: String(tempEnquiryData.phone || "Not provided").trim(),
      message: String(tempEnquiryData.message || "").trim(),
      product_list: enquiryProducts
        .map((p) => {
          const name = String(p.name || "Unnamed Product").trim();
          const price = `$${Number(p.price || 0).toFixed(2)}`;
          const image =
            p.image && /^https?:\/\/.+\..+/.test(p.image)
              ? String(p.image).trim()
              : "https://via.placeholder.com/64";
          return `<tr>
                    <td style="padding: 8px; vertical-align: middle;">
                      <img src="${image}" alt="${name}" height="64" style="height: 64px; width: 64px; object-fit: cover;" />
                    </td>
                    <td style="padding: 8px; vertical-align: middle;">
                      <strong>${name}</strong>
                    </td>
                    <td style="padding: 8px; vertical-align: middle;">
                      ${price}
                    </td>
                  </tr>`;
        })
        .join(""),
    };

    console.log(
      "Sending templateParams:",
      JSON.stringify(templateParams, null, 2)
    );

    if (
      !process.env.REACT_APP_EMAILJS_SERVICE_ID ||
      !process.env.REACT_APP_EMAILJS_TEMPLATE_ID ||
      !process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    ) {
      alert("Configuration error: Email service is not properly set up.");
      return;
    }

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(
        (result) => {
          alert("Enquiry sent successfully!");
          setEnquiryItems((prev) => [
            ...prev,
            {
              products: enquiryProducts.map((p) => ({
                id: p.id,
                name: p.name,
                price: p.price,
                image: p.image,
              })),
              customer: { ...tempEnquiryData },
              date: new Date().toISOString(),
            },
          ]);
          handleClearForm();
        },
        (error) => {
          console.error("EmailJS error details:", {
            text: error.text,
            status: error.status,
            response: error.response,
          });
          alert("Failed to send enquiry. Please try again.");
        }
      );
  };

  const removeProduct = (productId) => {
    setEnquiryProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const handleClearForm = () => {
    setTempEnquiryData({
      name: "",
      email: "",
      phone: "",
      message: "",
    });
    setEnquiryProducts([]);
  };

  const handleOutsideClick = (e) => {
    if (modalRef.current && e.target === e.currentTarget) {
      setEnquiryModalOpen(false);
    }
  };

  useEffect(() => {
    if (enquiryModalOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.body.style.overflow = "unset";
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.body.style.overflow = "unset";
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [enquiryModalOpen]);

  if (!enquiryModalOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleOutsideClick}
    >
      <div
        ref={modalRef}
        className="relative bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              Product Enquiry ({enquiryProducts.length})
            </h2>
            <button
              onClick={() => setEnquiryModalOpen(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl"
              aria-label="Close enquiry modal"
            >
              ×
            </button>
          </div>

          {enquiryProducts.length > 0 ? (
            <div className="mb-6">
              <h3 className="font-medium mb-2">Selected Products:</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {enquiryProducts.map((product) => (
                  <div
                    key={product.id}
                    className="flex justify-between items-center p-2 border rounded"
                  >
                    <div className="flex items-center">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-12 h-12 object-cover mr-3"
                      />
                      <div>
                        <h4 className="font-medium">{product.name}</h4>
                        <p className="text-sm text-gray-600">
                          ${product.price.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeProduct(product.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-6 p-3 bg-gray-50 rounded text-center">
              <p>No products selected for enquiry</p>
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 mb-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium mb-1"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={tempEnquiryData.name}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={tempEnquiryData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium mb-1"
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={tempEnquiryData.phone}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Optional phone number"
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-sm font-medium mb-1"
              >
                Message *
              </label>
              <textarea
                id="message"
                required
                value={tempEnquiryData.message}
                onChange={handleInputChange}
                className="w-full p-2 border rounded min-h-[100px] focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Please include any specific details about your enquiry..."
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={() => setEnquiryModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Proceed to Enquiry
              </button>
              <button
                type="button"
                onClick={handleClearForm}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Clear Form
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                Submit Enquiry
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EnquiryModal;
